import React, { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useBilling } from '../../hooks/useBilling';
import { AuthContext } from '../../contexts/AuthContext';
import { isPaymentFailed } from '../../utils/onboardingStatus';
import { getPlanTier } from '../../utils/authAccess';
import analyticsService from '../../services/analyticsService';
import { BILLING_PLANS, PRO_GOLD, PRO_GOLD_DEEP, formatPlanPriceDisplay, proGoldRgba } from './billingPlanData';

const PLAN_DISPLAY = {
  track: {
    name: BILLING_PLANS.track.displayName,
    price: 'Free',
  },
  trade: {
    name: BILLING_PLANS.trade.displayName,
    price: `${formatPlanPriceDisplay(BILLING_PLANS.trade.priceMonthly)}/mo`,
  },
};

/**
 * Full-screen blocking overlay shown when the user's trial or subscription has ended.
 * Non-dismissible — exit by subscribing, updating payment, or taking the one-time extra week.
 */
export default function TrialExpiredModal({ show }) {
  const { colors } = useTheme();
  const { user, refreshUserSettings } = useContext(AuthContext);
  const { createCheckoutSession, createPortalSession, extendTrial, loading } = useBilling();
  const [busy, setBusy] = useState(false);
  const [extending, setExtending] = useState(false);
  const [extendError, setExtendError] = useState('');

  if (!show) return null;

  const paymentFailed = isPaymentFailed(user);
  const canExtendTrial = !paymentFailed && user?.subscriptionStatus?.canExtendTrial === true;
  const planTier = getPlanTier(user);
  const plan = PLAN_DISPLAY[planTier] || PLAN_DISPLAY.trade;
  const isEssentials = planTier === 'track' || planTier === 'essentials';
  const accentColor = isEssentials ? '#6366f1' : PRO_GOLD;

  const handleAction = async () => {
    setBusy(true);
    analyticsService.trackTradingEvent(
      paymentFailed ? 'trial_expired_update_payment_clicked' : 'trial_expired_subscribe_clicked',
      { user_id: user?.id, plan_tier: planTier }
    );
    try {
      if (paymentFailed) {
        await createPortalSession();
      } else {
        await createCheckoutSession('monthly', false, { planTier });
      }
    } catch (e) {
      console.error('TrialExpiredModal action error:', e);
      setBusy(false);
    }
  };

  const handleExtendTrial = async () => {
    setExtending(true);
    setExtendError('');
    analyticsService.trackTradingEvent('trial_expired_extend_week_clicked', {
      user_id: user?.id,
      plan_tier: planTier,
    });
    try {
      await extendTrial();
      const settings = refreshUserSettings ? await refreshUserSettings() : null;
      if (settings?.subscriptionStatus?.hasAccess !== true) {
        setExtendError('Your extra week is ready. Reload the page to continue.');
        setExtending(false);
      }
    } catch (e) {
      console.error('TrialExpiredModal extend error:', e);
      setExtendError(e?.message || 'Could not add your extra week. Please try again.');
      setExtending(false);
    }
  };

  const checkoutBusy = busy || (loading && !extending);
  const actionsLocked = checkoutBusy || extending;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="trial-expired-title"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 11000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        background: 'rgba(5, 7, 14, 0.88)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
      }}
    >
      <div
        style={{
          maxWidth: 480,
          width: '100%',
          background: 'linear-gradient(135deg, rgba(15, 20, 35, 0.98) 0%, rgba(8, 10, 20, 0.99) 100%)',
          borderRadius: 20,
          border: `1px solid ${paymentFailed ? 'rgba(239, 68, 68, 0.3)' : 'rgba(148, 163, 184, 0.15)'}`,
          boxShadow: '0 32px 64px -16px rgba(0, 0, 0, 0.7)',
          padding: '40px 36px',
          textAlign: 'center',
        }}
      >
        {/* Icon */}
        <div
          style={{
            width: 64,
            height: 64,
            margin: '0 auto 20px',
            borderRadius: 16,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 32,
            background: paymentFailed
              ? 'rgba(239, 68, 68, 0.12)'
              : `linear-gradient(135deg, ${isEssentials ? 'rgba(99,102,241,0.15)' : proGoldRgba(0.15)} 0%, rgba(139, 92, 246, 0.1) 100%)`,
            border: `1px solid ${paymentFailed ? 'rgba(239,68,68,0.25)' : `${accentColor}33`}`,
          }}
        >
          {paymentFailed ? '💳' : '⏱'}
        </div>

        {/* Title */}
        <h2
          id="trial-expired-title"
          style={{
            fontSize: '1.5rem',
            fontWeight: 700,
            marginBottom: 12,
            color: '#f1f5f9',
            letterSpacing: '-0.02em',
            lineHeight: 1.2,
          }}
        >
          {paymentFailed ? 'Payment Failed' : 'Your trial has ended'}
        </h2>

        {/* Body */}
        <p
          style={{
            fontSize: '0.9375rem',
            color: '#94a3b8',
            lineHeight: 1.65,
            marginBottom: 28,
          }}
        >
          {paymentFailed
            ? "We couldn't process your last payment. Update your payment method to restore access."
            : `Subscribe to ${plan.name} to keep your rules, journal, and settings — and pick up right where you left off.`}
        </p>

        {/* Plan badge */}
        {!paymentFailed && (
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              background: `${accentColor}18`,
              border: `1px solid ${accentColor}40`,
              borderRadius: 8,
              padding: '7px 16px',
              marginBottom: 24,
              fontSize: 13,
              fontWeight: 600,
              color: accentColor,
            }}
          >
            <span>{plan.name}</span>
            <span style={{ opacity: 0.7 }}>·</span>
            <span>{plan.price}</span>
          </div>
        )}

        {/* CTA */}
        <button
          type="button"
          onClick={handleAction}
          disabled={actionsLocked}
          style={{
            width: '100%',
            padding: '14px 24px',
            fontSize: '1rem',
            fontWeight: 700,
            color: paymentFailed ? '#fff' : (isEssentials ? '#fff' : '#1a1a2e'),
            background: paymentFailed
              ? 'linear-gradient(135deg, #ef4444 0%, #f59e0b 100%)'
              : isEssentials
                ? 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)'
                : `linear-gradient(135deg, ${PRO_GOLD} 0%, ${PRO_GOLD_DEEP} 100%)`,
            border: 'none',
            borderRadius: 12,
            cursor: actionsLocked ? 'not-allowed' : 'pointer',
            opacity: actionsLocked ? 0.7 : 1,
            transition: 'opacity 0.2s, transform 0.15s',
            boxShadow: paymentFailed
              ? '0 4px 14px rgba(239,68,68,0.35)'
              : `0 4px 14px ${accentColor}55`,
          }}
          onMouseEnter={(e) => {
            if (!actionsLocked) e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          {checkoutBusy
            ? 'Redirecting…'
            : paymentFailed
              ? 'Update Payment Method'
              : 'Subscribe Now'}
        </button>

        {canExtendTrial && (
          <button
            type="button"
            onClick={handleExtendTrial}
            disabled={actionsLocked}
            style={{
              width: '100%',
              marginTop: 12,
              padding: '12px 16px',
              fontSize: '0.875rem',
              fontWeight: 600,
              lineHeight: 1.45,
              color: '#e2e8f0',
              background: 'rgba(148, 163, 184, 0.08)',
              border: '1px solid rgba(148, 163, 184, 0.28)',
              borderRadius: 12,
              cursor: actionsLocked ? 'not-allowed' : 'pointer',
              opacity: actionsLocked ? 0.7 : 1,
            }}
          >
            {extending
              ? 'Adding your week…'
              : "Didn't get to test drive it? Here's another week, on us!"}
          </button>
        )}

        {extendError && (
          <p
            role="alert"
            style={{
              marginTop: 12,
              marginBottom: 0,
              fontSize: 13,
              color: '#f87171',
              lineHeight: 1.45,
            }}
          >
            {extendError}
          </p>
        )}

        {!paymentFailed && (
          <p style={{ marginTop: 14, marginBottom: 0, fontSize: '0.875rem' }}>
            <Link
              to="/pricing"
              style={{ color: colors.primary, fontWeight: 500, textDecoration: 'none' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.textDecoration = 'underline';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.textDecoration = 'none';
              }}
            >
              Change plans
            </Link>
          </p>
        )}

        <p
          style={{
            marginTop: 16,
            fontSize: 11,
            color: '#64748b',
            lineHeight: 1.5,
          }}
        >
          Cancel anytime. Your data is saved.
        </p>
      </div>
    </div>
  );
}
